import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/shared/cn';
import { Commentable } from '@/shared/comments/Commentable';
import {
  SidebarRail,
  SidebarRailButton,
  SidebarRow,
  SidebarShell,
  SIDEBAR_SECTION_LABEL,
} from '@/versions/v1/chrome/sidebar/SidebarShell';
import { SidebarToggle } from '@/versions/v1/chrome/sidebar/SidebarToggle';
import { BrandMark } from '@/versions/v1/shared/BrandMark';
import {
  activeNavId,
  navBadge,
  visibleSections,
  type DemoMode,
  type OemNavSection,
  type OemSectionId,
} from '@/versions/v1/shared/oem-nav';
import { FLEET_HEADER, OEM_STATIC_COUNTS } from '@/versions/v1/shared/oem-counts';
import { useDemoMode } from '@/versions/v1/shared/DemoModeContext';

// =========================================================================
// OemSidebar — the demo's `nav.side`, rebuilt on the foundation's sidebar
// primitives. Fork-owned (zone C); reached through the one-line default
// branch in chrome/AppSidebar.tsx.
//
// Structure mirrors the demo: a fleet header block, then eight collapsible
// sections driven entirely by shared/oem-nav.ts, then the foundation's
// AccountMenu as the footer (SidebarShell supplies that when `footer` is
// omitted, so the brand + theme + sign-out menu comes for free).
//
// Collapsed-rail behaviour is the foundation's: one icon per section head,
// linking to that section's first item.
// =========================================================================

/** A row's badge pill. `hot` is the demo's critical treatment. */
function NavBadge({ count, hot }: { count: number; hot?: boolean }) {
  return (
    <span
      className="tabular rounded-full px-1.5 py-px text-[10px] font-medium leading-[1.5]"
      style={
        hot
          ? { background: 'var(--crit-soft)', color: 'var(--crit)' }
          : { background: 'var(--surface-3)', color: 'var(--ink-tertiary)' }
      }
    >
      {count}
    </span>
  );
}

/** A section is open when the active row belongs to it. */
function sectionHasActive(activeId: string | null, section: OemNavSection): boolean {
  return activeId !== null && section.items.some((item) => item.id === activeId);
}

// The shared SIDEBAR_SECTION_LABEL pairs text-zinc-400 with dark:text-zinc-500,
// which measures ~2:1 against the dark sidebar field — below the WCAG AA 4.5:1
// floor for the small uppercase type these headings use. The ink tokens are
// already theme-aware (see styles/tokens.css), so one unprefixed class covers
// both themes once the dark: override is merged away. ThreadHistorySidebar is a
// parity snapshot target and keeps the shared constant untouched.
const OEM_SECTION_LABEL = cn(
  SIDEBAR_SECTION_LABEL,
  'text-ink-secondary dark:text-ink-secondary',
);

/** Decorative section glyph — non-text contrast only, so tertiary ink is enough. */
const OEM_SECTION_ICON = 'h-3.5 w-3.5 flex-shrink-0 text-ink-tertiary';

function NavSection({
  section,
  activeId,
  mode,
  open,
  onToggle,
}: {
  section: OemNavSection;
  activeId: string | null;
  mode: DemoMode;
  open: boolean;
  onToggle: () => void;
}) {
  const SectionIcon = section.icon;

  return (
    <div className="mt-1">
      <div className="mb-1 mt-3 px-3">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={`oem-nav-${section.id}`}
          className="flex w-full cursor-pointer items-center gap-2 rounded px-0 py-1 transition-[background-color,box-shadow] hover:bg-zinc-900/[0.04] dark:hover:bg-white/[0.04]"
        >
          <SectionIcon
            className={OEM_SECTION_ICON}
            strokeWidth={1.7}
            aria-hidden="true"
          />
          <span className={cn(OEM_SECTION_LABEL, 'truncate')}>
            {section.label}
          </span>
          <span className="ml-auto flex h-5 w-5 shrink-0 items-center justify-center text-ink-tertiary">
            {open ? (
              <ChevronDown className="h-3 w-3" strokeWidth={2} aria-hidden="true" />
            ) : (
              <ChevronRight className="h-3 w-3" strokeWidth={2} aria-hidden="true" />
            )}
          </span>
        </button>
      </div>

      {open ? (
        <ul id={`oem-nav-${section.id}`} className="space-y-0">
          {section.items.map((item) => (
            <li key={item.id} className={item.sub ? 'ml-4' : undefined}>
              <SidebarRow
                href={item.to}
                icon={item.icon}
                label={item.label}
                isActive={item.id === activeId}
                trailing={(() => {
                  const count = navBadge(item, OEM_STATIC_COUNTS, mode);
                  return count === undefined ? undefined : (
                    <NavBadge count={count} hot={item.hot} />
                  );
                })()}
              />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function OemSidebar() {
  const { pathname, search } = useLocation();
  // fork(P7a/A1): the rail is the one caller that asks for a mode. Everything
  // else — crumbs, palette, check scripts — keeps seeing the whole nav.
  const { mode } = useDemoMode();
  const sections = useMemo(() => visibleSections(mode), [mode]);
  const activeId = useMemo(
    () => activeNavId(pathname, search, mode),
    [pathname, search, mode],
  );

  // Seed open state from oem-nav's `defaultOpen`; a section holding the
  // current route is forced open regardless, so a deep link never lands on a
  // page whose nav row is hidden.
  const [manual, setManual] = useState<Partial<Record<OemSectionId, boolean>>>({});

  // fork(P7a/A7): the two modes are two different rails, so a manual
  // open/closed override earned in one does not carry into the other.
  // Without this, flipping to Full and back leaves the executive rail in
  // whatever shape the full one was left in — and the first thing the room
  // sees on a flip back is meant to be Home and Issues, nothing else.
  useEffect(() => {
    setManual({});
  }, [mode]);

  const isOpen = useCallback(
    (section: OemNavSection) =>
      manual[section.id] ?? (section.defaultOpen || sectionHasActive(activeId, section)),
    [manual, activeId],
  );

  const toggle = useCallback(
    (section: OemNavSection) => {
      setManual((prev) => ({
        ...prev,
        [section.id]: !(
          prev[section.id] ??
          (section.defaultOpen || sectionHasActive(activeId, section))
        ),
      }));
    },
    [activeId],
  );

  const collapsedRail = (
    <SidebarRail>
      {sections.map((section) => (
        <SidebarRailButton
          key={section.id}
          href={section.items[0].to}
          icon={section.icon}
          label={section.label}
          isActive={sectionHasActive(activeId, section)}
        />
      ))}
    </SidebarRail>
  );

  return (
    <Commentable name="OemSidebar">
      <SidebarShell
        ariaLabel="OEM+ navigation"
        collapsedRail={collapsedRail}
        header={
          <>
            <BrandMark variant="wordmark" className="min-w-0 truncate text-sm" />
            <SidebarToggle variant="sidebar" />
          </>
        }
        subheader={
          /* fork(P7a/A5): one line, not three. The eyebrow repeated the
             wordmark above it and the six mine names repeated the Mine sites
             section below it — together about 130 px of rail before the first
             nav row, saying nothing `/sites` does not say better. */
          <div className="px-3 pb-2 pt-0.5">
            <div className="text-[12px] font-medium leading-snug text-muted">
              {FLEET_HEADER.title}
            </div>
          </div>
        }
      >
        <nav aria-label="OEM+ sections" className="pb-2">
          {sections.map((section) => (
            <NavSection
              key={section.id}
              section={section}
              activeId={activeId}
              mode={mode}
              open={isOpen(section)}
              onToggle={() => toggle(section)}
            />
          ))}
        </nav>
      </SidebarShell>
    </Commentable>
  );
}
